// Mobile view-mode hook (T-252, mobile-view-mode-spec.md §2 + §3).
//
// Decides which mobile chrome to render:
//   - simple   — two big CTAs (report vacation, report sick) plus
//                a manager-only approvals list. Default.
//   - advanced — the existing responsive AppShell with the hamburger
//                drawer + standard routes.
//
// `isMobile` flips at the 768px breakpoint (one step narrower than the
// sidebar's 1024px cutoff — tablet landscape keeps the existing
// responsive layout per spec §2). The hook subscribes to matchMedia so
// a phone rotated to landscape recomputes without a reload.
//
// `viewMode` defaults to `'simple'` per the owner's brief and persists
// in localStorage under `mobile.viewMode`. Both reads and writes are
// `try/catch`'d — Safari Private Browsing throws on localStorage.

import { useCallback, useEffect, useState } from 'react';

export type MobileViewMode = 'simple' | 'advanced';

const STORAGE_KEY = 'mobile.viewMode';
// We query the *non-mobile* boundary (`min-width: 768px`) and invert
// the result. Matches the `useSidebarState` idiom and plays nicely
// with the shared `matchMedia` stubs in AppShell tests, which return
// the same `matches` value for any query.
const NON_MOBILE_MIN_PX = 768;

export interface MobileViewModeState {
  isMobile: boolean;
  viewMode: MobileViewMode;
  setViewMode: (next: MobileViewMode) => void;
}

function readPersisted(): MobileViewMode {
  if (typeof window === 'undefined') return 'simple';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'advanced') return 'advanced';
    return 'simple';
  } catch {
    return 'simple';
  }
}

function writePersisted(value: MobileViewMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Private browsing / disabled storage — in-memory state still
    // flips for the session, only persistence is lost.
  }
}

function readIsMobile(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return !window.matchMedia(`(min-width: ${NON_MOBILE_MIN_PX}px)`).matches;
}

export function useMobileViewMode(): MobileViewModeState {
  const [viewMode, setViewModeState] = useState<MobileViewMode>(readPersisted);
  const [isMobile, setIsMobile] = useState<boolean>(readIsMobile);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(
      `(min-width: ${NON_MOBILE_MIN_PX}px)`,
    );
    function onChange(e: MediaQueryListEvent | MediaQueryList) {
      setIsMobile(!e.matches);
    }
    setIsMobile(!mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const setViewMode = useCallback((next: MobileViewMode) => {
    setViewModeState(next);
    writePersisted(next);
  }, []);

  return { isMobile, viewMode, setViewMode };
}
